import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { getApiErrorMessage } from '../../api';
import {
  fuelingService,
  type Fueling,
  type FuelingStatus,
} from '../../api/fuelings';
import {
  Fuel,
  Search,
  RefreshCw,
  Clock,
  CheckCircle2,
  ShieldCheck,
  Car,
  User,
  Building2,
  Gauge,
  CalendarDays,
  XCircle,
  Eye,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';
import {
  PageHeader,
  Button,
  Badge,
  Input,
  SectionCard,
  CardHeader,
  CardBody,
  ModernTable,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
} from '../../components/ui/SharedLayout';

const formatCurrency = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value || 0);

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleString('pt-BR');
};

const ITEMS_PER_PAGE = 20;

const ClientAbastecimento = () => {
  const { setPageTitle } = useAuth();
  const [fuelings, setFuelings] = useState<Fueling[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingId, setUpdatingId] = useState<number | null>(null);
  const [selectedFueling, setSelectedFueling] = useState<Fueling | null>(null);

  useEffect(() => {
    setPageTitle('Abastecimentos do setor');
  }, [setPageTitle]);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await fuelingService.getAllFuelings();
      setFuelings(data);
    } catch (loadError) {
      setFuelings([]);
      setError(
        getApiErrorMessage(loadError, 'Erro ao carregar abastecimentos do setor.'),
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const updateStatus = async (fueling: Fueling, status: FuelingStatus) => {
    setUpdatingId(fueling.id);
    setError(null);

    try {
      const updatedFueling = await fuelingService.updateFueling(fueling.id, { status });
      setFuelings((current) =>
        current.map((item) => (item.id === fueling.id ? updatedFueling : item)),
      );
      if (selectedFueling?.id === fueling.id) {
        setSelectedFueling(updatedFueling);
      }
    } catch (updateError) {
      setError(getApiErrorMessage(updateError, 'Erro ao atualizar o status do abastecimento.'));
    } finally {
      setUpdatingId(null);
    }
  };

  const filteredFuelings = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return fuelings.filter((fueling) => {
      if (!term) return true;

      return [
        fueling.vehicle?.brand || '',
        fueling.vehicle?.license_plate || '',
        fueling.employee?.name || '',
        fueling.employee?.cpf || '',
        fueling.provider?.name || '',
        fueling.fuel_type || '',
        fueling.status || '',
      ]
        .join(' ')
        .toLowerCase()
        .includes(term);
    });
  }, [fuelings, searchTerm]);

  const pendingCount = useMemo(
    () => fuelings.filter((f) => f.status === 'blocked').length,
    [fuelings],
  );

  const totalPages = Math.max(1, Math.ceil(filteredFuelings.length / ITEMS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [filteredFuelings.length, currentPage, totalPages]);

  const { paginatedFuelings, pageRange } = useMemo(() => {
    const total = filteredFuelings.length;
    const safePage = Math.min(currentPage, totalPages);
    const startIndex = (safePage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const sliced = filteredFuelings.slice(startIndex, endIndex);
    return {
      paginatedFuelings: sliced,
      pageRange: {
        start: total === 0 ? 0 : startIndex + 1,
        end: Math.min(endIndex, total),
        total,
      },
    };
  }, [filteredFuelings, currentPage, totalPages]);

  const getPageNumbers = useMemo(() => {
    const pages: (number | '...')[] = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
      return pages;
    }
    pages.push(1);
    const left = Math.max(2, currentPage - 1);
    const right = Math.min(totalPages - 1, currentPage + 1);
    if (left > 2) pages.push('...');
    for (let i = left; i <= right; i++) pages.push(i);
    if (right < totalPages - 1) pages.push('...');
    pages.push(totalPages);
    return pages;
  }, [currentPage, totalPages]);

  const renderStatusBadge = (status: string) => {
    switch (status) {
      case 'aprovado':
        return (
          <Badge tone="emerald" className="gap-1.5">
            <CheckCircle2 className="h-3 w-3 stroke-[2]" />
            Aprovado
          </Badge>
        );
      case 'rejeitado':
        return (
          <Badge tone="red" className="gap-1.5">
            <XCircle className="h-3 w-3 stroke-[2]" />
            Rejeitado
          </Badge>
        );
      case 'finalizado':
        return (
          <Badge tone="slate" className="gap-1.5">
            <ShieldCheck className="h-3 w-3 stroke-[2]" />
            Finalizado
          </Badge>
        );
      case 'blocked':
      default:
        return (
          <Badge tone="amber" className="gap-1.5">
            <Clock className="h-3 w-3 stroke-[2]" />
            Pendente
          </Badge>
        );
    }
  };

  return (
    <div className="w-full">
      <div className="mx-auto w-full max-w-[1440px] px-4 sm:px-6 lg:px-8 py-7">
        <PageHeader title="Abastecimentos do setor" hideTitleInBody />

        <div className="mb-7 flex flex-col gap-1.5 sm:flex-row sm:items-start sm:justify-between">
          <div className="space-y-1.5 max-w-2xl">
            <p className="text-[15px] font-semibold leading-tight text-sky-600">
              Operações — Abastecimentos
            </p>
            <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
              <h1 className="text-[22px] sm:text-[26px] font-bold tracking-tight leading-tight text-slate-900">
                Aprove e acompanhe os abastecimentos
              </h1>
              <span className="text-[18px] sm:text-[20px] font-medium text-slate-500 leading-tight">
                da sua equipe e departamento
              </span>
            </div>
          </div>
          <div className="flex flex-col-reverse sm:flex-row sm:items-center gap-2 w-full sm:w-auto mt-1 sm:mt-0">
            {pendingCount > 0 && (
              <div className="inline-flex h-10 items-center gap-1.5 rounded-lg border border-amber-200 bg-amber-50 px-3 text-[13px] font-bold text-amber-700">
                <Clock className="h-4 w-4 stroke-[1.9]" />
                Pendentes: {pendingCount}
              </div>
            )}
            <Button variant="secondary" onClick={() => void loadData()} className="h-10 px-4">
              <RefreshCw className="h-4 w-4 stroke-[1.8]" />
              Atualizar
            </Button>
          </div>
        </div>

        {error && (
          <div className="mb-5 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-800">
            {error}
          </div>
        )}

        <SectionCard>
          <CardHeader>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-orange-500 via-amber-500 to-orange-600 text-white shadow-[0_6px_16px_-8px_rgba(249,115,22,0.6)]">
                <Fuel className="h-[18px] w-[18px] stroke-[1.9]" />
              </div>
              <div>
                <h2 className="text-[15px] font-bold tracking-tight text-slate-900">
                  Abastecimentos
                </h2>
                <p className="text-[13px] text-slate-500 mt-0.5">
                  {loading
                    ? 'Carregando registros...'
                    : pageRange.total === 0
                      ? 'Nenhum abastecimento encontrado'
                      : `${pageRange.start} a ${pageRange.end} de ${pageRange.total} abastecimentos listados (total cadastrado: ${fuelings.length})`}
                </p>
              </div>
            </div>
            <div className="w-full sm:w-[360px] shrink-0 relative">
              <Search
                className="pointer-events-none absolute left-3.5 top-1/2 h-4 w-4 -translate-y-1/2 stroke-[1.8] text-slate-400"
                aria-hidden="true"
              />
              <Input
                type="text"
                name="search-fuelings"
                placeholder="Pesquisar veículo, motorista, fornecedor, combustível..."
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setCurrentPage(1);
                }}
                className="!pl-10"
              />
            </div>
          </CardHeader>

          <CardBody className="!py-3">
            {loading ? (
              <div className="flex justify-center items-center py-16">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                <span className="ml-3 text-sm font-medium text-slate-600">
                  Carregando abastecimentos...
                </span>
              </div>
            ) : (
              <Fragment>
                <ModernTable>
                  <Thead>
                    <Tr>
                      <Th>Veículo</Th>
                      <Th>Motorista</Th>
                      <Th>Fornecedor</Th>
                      <Th>Combustível</Th>
                      <Th>Litros</Th>
                      <Th>Valor</Th>
                      <Th>Data</Th>
                      <Th>Status</Th>
                      <Th>Ações</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {pageRange.total === 0 ? (
                      <tr>
                        <td
                          colSpan={9}
                          className="px-5 py-14 text-center text-sm text-slate-500"
                        >
                          Nenhum abastecimento encontrado
                        </td>
                      </tr>
                    ) : (
                      paginatedFuelings.map((fueling) => {
                        const isPending = fueling.status === 'blocked';
                        return (
                          <Tr key={fueling.id}>
                            <Td>
                              <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 min-w-[40px] shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-sky-500 via-blue-500 to-blue-700 text-white shadow-[0_6px_16px_-8px_rgba(59,130,246,0.6)]">
                                  <Car className="h-[18px] w-[18px] min-h-[18px] min-w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-semibold text-slate-900">
                                    {fueling.vehicle?.brand || `Veículo #${fueling.vehicle_id}`}
                                  </div>
                                  <div className="mt-0.5 font-mono text-[12px] tracking-wide text-slate-500 truncate">
                                    {fueling.vehicle?.license_plate || '-'}
                                  </div>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="flex items-center gap-3">
                                <div className="flex h-10 w-10 min-w-[40px] shrink-0 items-center justify-center rounded-[10px] bg-gradient-to-br from-emerald-500 via-teal-500 to-emerald-600 text-white shadow-[0_6px_16px_-8px_rgba(16,185,129,0.6)]">
                                  <User className="h-[18px] w-[18px] min-h-[18px] min-w-[18px] stroke-[1.9]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <div className="truncate font-semibold text-slate-900">
                                    {fueling.employee?.name || `Motorista #${fueling.employee_id}`}
                                  </div>
                                  <div className="mt-0.5 text-[12px] text-slate-500 truncate">
                                    {fueling.employee?.cpf || '-'}
                                  </div>
                                </div>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1.5">
                                <Building2 className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                                <span className="truncate font-medium text-slate-700">
                                  {fueling.provider?.name || `Fornecedor #${fueling.provider_id}`}
                                </span>
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1 rounded-md bg-orange-50 px-2.5 py-1 text-[12px] font-bold text-orange-700 ring-1 ring-inset ring-orange-200">
                                <Fuel className="h-3 w-3 stroke-[1.9]" />
                                {fueling.fuel_type || '-'}
                              </div>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1.5">
                                <Gauge className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                                <span className="font-semibold text-slate-700 tabular-nums">
                                  {fueling.amount?.toLocaleString('pt-BR') || 0}L
                                </span>
                              </div>
                            </Td>
                            <Td>
                              <span className="text-[13px] font-bold text-emerald-700 tabular-nums">
                                {formatCurrency(fueling.total_cost)}
                              </span>
                            </Td>
                            <Td>
                              <div className="inline-flex items-center gap-1.5">
                                <CalendarDays className="h-3.5 w-3.5 stroke-[1.8] text-slate-400" />
                                <span className="font-medium text-slate-700 tabular-nums text-[12.5px]">
                                  {formatDate(fueling.fueling_time || fueling.created_at)}
                                </span>
                              </div>
                            </Td>
                            <Td>
                              {renderStatusBadge(fueling.status ?? 'blocked')}
                            </Td>
                            <Td>
                              <div className="flex items-center gap-1.5">
                                <Button
                                  type="button"
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => setSelectedFueling(fueling)}
                                  className="!h-8 !px-2"
                                  title="Visualizar detalhes"
                                >
                                  <Eye className="h-4 w-4 stroke-[1.9]" />
                                </Button>
                                {isPending && (
                                  <>
                                    <Button
                                      type="button"
                                      variant="primary"
                                      size="sm"
                                      onClick={() => void updateStatus(fueling, 'aprovado')}
                                      disabled={updatingId === fueling.id}
                                      className="!h-8 !px-2 !bg-emerald-600 hover:!bg-emerald-700"
                                      title="Aprovar abastecimento"
                                    >
                                      <Check className="h-4 w-4 stroke-[2.1]" />
                                    </Button>
                                    <Button
                                      type="button"
                                      variant="primary"
                                      size="sm"
                                      onClick={() => void updateStatus(fueling, 'rejeitado')}
                                      disabled={updatingId === fueling.id}
                                      className="!h-8 !px-2 !bg-red-600 hover:!bg-red-700"
                                      title="Rejeitar abastecimento"
                                    >
                                      <X className="h-4 w-4 stroke-[2.1]" />
                                    </Button>
                                  </>
                                )}
                              </div>
                            </Td>
                          </Tr>
                        );
                      })
                    )}
                  </Tbody>
                </ModernTable>

                {!loading && (
                  <div className="mt-5 flex flex-col gap-3 border-t border-slate-200 pt-4 sm:flex-row sm:items-center sm:justify-between">
                    <div className="text-[13px] font-medium text-slate-500">
                      Mostrando <span className="font-semibold text-slate-700">{pageRange.start}-{pageRange.end}</span> de <span className="font-semibold text-slate-700">{pageRange.total}</span> resultados
                    </div>
                    <div className="inline-flex items-center gap-1.5">
                      <button
                        type="button"
                        onClick={() => setCurrentPage(1)}
                        disabled={currentPage === 1}
                        aria-label="Primeira página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                        aria-label="Página anterior"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronLeft className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <div className="inline-flex items-center gap-1 mx-1">
                        {getPageNumbers.map((n, idx) =>
                          n === '...' ? (
                            <span
                              key={`dots-${idx}`}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg text-slate-500 text-[13px]"
                            >
                              …
                            </span>
                          ) : (
                            <button
                              key={n}
                              type="button"
                              onClick={() => setCurrentPage(n)}
                              aria-label={`Ir para página ${n}`}
                              className={`inline-flex h-9 min-w-[36px] cursor-pointer items-center justify-center rounded-lg px-2.5 text-[13px] font-semibold transition-all duration-200 disabled:cursor-not-allowed disabled:opacity-60 ${
                                n === currentPage
                                  ? 'bg-blue-600 text-white shadow-[0_4px_14px_-4px_rgba(37,99,235,0.55)]'
                                  : 'border border-slate-200 bg-white text-slate-700 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700'
                              }`}
                            >
                              {n}
                            </button>
                          ),
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                        aria-label="Próxima página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        aria-label="Última página"
                        className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all duration-200 hover:border-blue-300 hover:bg-blue-50 hover:text-blue-700 disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:border-slate-200 disabled:hover:bg-white disabled:hover:text-slate-500"
                      >
                        <ChevronsRight className="h-[16px] w-[16px] stroke-[1.8]" />
                      </button>
                    </div>
                  </div>
                )}
              </Fragment>
            )}
          </CardBody>
        </SectionCard>
      </div>

      {selectedFueling && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-black bg-opacity-60 px-4 py-6">
          <div className="flex min-h-full items-center justify-center">
            <div className="w-full max-w-3xl rounded-2xl bg-white shadow-2xl">
              <div className="flex items-start justify-between gap-4 border-b border-slate-200 px-8 py-5">
                <div>
                  <h2 className="text-[20px] font-bold text-slate-900">
                    Detalhes do Abastecimento
                  </h2>
                  <p className="mt-1 text-[13px] text-slate-500">
                    Registro #{selectedFueling.id} • Atualizado em{' '}
                    {formatDate(selectedFueling.updated_at || selectedFueling.created_at)}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setSelectedFueling(null)}
                  className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 transition-all hover:border-rose-300 hover:bg-rose-50 hover:text-rose-700"
                  aria-label="Fechar detalhes"
                >
                  <X className="h-4 w-4 stroke-[1.9]" />
                </button>
              </div>
              <div className="max-h-[80vh] overflow-y-auto p-8">
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Veículo</p>
                    <p className="mt-1.5 text-[14.5px] font-bold text-slate-900">
                      {selectedFueling.vehicle?.brand || `Veículo #${selectedFueling.vehicle_id}`}
                    </p>
                    <p className="mt-0.5 text-[12.5px] text-slate-600 font-mono">
                      {selectedFueling.vehicle?.license_plate || '-'}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Motorista</p>
                    <p className="mt-1.5 text-[14.5px] font-bold text-slate-900">
                      {selectedFueling.employee?.name || `Motorista #${selectedFueling.employee_id}`}
                    </p>
                    <p className="mt-0.5 text-[12.5px] text-slate-600">
                      {selectedFueling.employee?.cpf || '-'}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Fornecedor</p>
                    <p className="mt-1.5 text-[14.5px] font-bold text-slate-900">
                      {selectedFueling.provider?.name || `Fornecedor #${selectedFueling.provider_id}`}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Situação Atual</p>
                    <div className="mt-1.5">{renderStatusBadge(selectedFueling.status ?? 'blocked')}</div>
                  </div>
                  <div className="rounded-xl border border-orange-200 bg-orange-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-orange-600">Combustível</p>
                    <p className="mt-1.5 text-[14.5px] font-bold text-orange-800">
                      {selectedFueling.fuel_type}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Quantidade (Litros)</p>
                    <p className="mt-1.5 text-[14.5px] font-bold text-slate-900 tabular-nums">
                      {selectedFueling.amount?.toLocaleString('pt-BR') || 0} L
                    </p>
                  </div>
                  <div className="rounded-xl border border-emerald-200 bg-emerald-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-emerald-700">Valor Total</p>
                    <p className="mt-1.5 text-[17px] font-black text-emerald-800 tabular-nums">
                      {formatCurrency(selectedFueling.total_cost)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Quilometragem</p>
                    <p className="mt-1.5 text-[14.5px] font-bold text-slate-900 tabular-nums">
                      {selectedFueling.odometer_reading
                        ? `${selectedFueling.odometer_reading.toLocaleString('pt-BR')} km`
                        : '-'}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Data/Hora do Abastecimento</p>
                    <p className="mt-1.5 text-[14.5px] font-bold text-slate-900 tabular-nums">
                      {formatDate(selectedFueling.fueling_time || selectedFueling.created_at)}
                    </p>
                  </div>
                  <div className="rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Nota Fiscal</p>
                    <p className="mt-1.5 text-[14.5px] font-bold text-slate-900 tabular-nums">
                      {selectedFueling.invoice_number || '-'}
                    </p>
                  </div>
                  <div className="md:col-span-2 rounded-xl border border-slate-200 bg-slate-50 p-4">
                    <p className="text-[11.5px] font-bold uppercase tracking-wide text-slate-500">Observações / Notas internas</p>
                    <p className="mt-1.5 text-[14px] font-medium text-slate-800 whitespace-pre-wrap leading-relaxed">
                      {selectedFueling.notes || 'Nenhuma observação cadastrada neste abastecimento.'}
                    </p>
                  </div>
                </div>

                <div className="mt-7 flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => setSelectedFueling(null)}
                    className="h-10 px-5"
                  >
                    Fechar
                  </Button>
                  {selectedFueling.status === 'blocked' && (
                    <>
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => void updateStatus(selectedFueling, 'rejeitado')}
                        disabled={updatingId === selectedFueling.id}
                        className="h-10 px-5 !bg-red-600 hover:!bg-red-700"
                      >
                        <XCircle className="h-4 w-4 stroke-[1.9]" />
                        Rejeitar abastecimento
                      </Button>
                      <Button
                        type="button"
                        variant="primary"
                        onClick={() => void updateStatus(selectedFueling, 'aprovado')}
                        disabled={updatingId === selectedFueling.id}
                        className="h-10 px-5 !bg-emerald-600 hover:!bg-emerald-700"
                      >
                        <CheckCircle2 className="h-4 w-4 stroke-[1.9]" />
                        Aprovar abastecimento
                      </Button>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ClientAbastecimento;
